import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import ViewListIcon from '@mui/icons-material/ViewList';
import { Box, Button, CircularProgress, InputBase, MenuItem, TextField } from '@mui/material';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { PreviewList } from '~/analytics/events';
import { UserContext } from '~/auth/userContext';
import ViewToggle from '~/components/common/ToggleButtonGroup';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { CompletionPrompt, useCompletionPrompts } from '~/components/tasks/CompletionPrompt';
import {
  ANY_TIME,
  CreatedDateFilter,
  type CreatedFilter,
} from '~/components/tasks/CreatedDateFilter';
import { NewTaskDialog } from '~/components/tasks/NewTaskDialog';
import { StatusesDialog } from '~/components/tasks/StatusesDialog';
import { TaskBoard } from '~/components/tasks/TaskBoard';
import {
  createdInFilter,
  type FollowUpFilter,
  hasActiveFilters,
  matchesTaskFilters,
  shownInPeriod,
  sortTasks,
  TASK_SORTS,
  type TaskSort,
  useTaskFilters,
} from '~/components/tasks/taskFilters';
import { TaskGroupedList } from '~/components/tasks/TaskGroupedList';
import { TaskPreviewPanel } from '~/components/tasks/TaskPreviewPanel';
import { inCreatedRange } from '~/components/tasks/taskStyle';
import { useGlobalFilterContext } from '~/contexts/globalFilterContext';
import type { TaskDetail, TaskSummary } from '~/data/generated/model';
import type { TaskScope } from '~/data/hooks/taskKeys';
import { useTasks } from '~/data/hooks/useTasks';
import { useTaskStatuses } from '~/data/hooks/useTaskStatuses';
import { BUTTON_SX, FILTER_CONTROL_SX } from '~/theme';

export type TasksLayout = 'board' | 'list';

const LAYOUTS = [
  {
    value: 'board',
    title: (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <ViewKanbanOutlinedIcon fontSize="small" /> Board
      </Box>
    ),
  },
  {
    value: 'list',
    title: (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <ViewListIcon fontSize="small" /> List
      </Box>
    ),
  },
];

const LAYOUT_STORAGE_KEY = 'liftai.tasks.layout';

function readLayout(): TasksLayout {
  try {
    return window.localStorage.getItem(LAYOUT_STORAGE_KEY) === 'list' ? 'list' : 'board';
  } catch {
    return 'board';
  }
}

function writeLayout(layout: TasksLayout) {
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
  } catch {
    return;
  }
}

const SORT_STORAGE_KEY = 'liftai.tasks.sort';

function readSort(): TaskSort {
  try {
    const stored = window.localStorage.getItem(SORT_STORAGE_KEY);
    return TASK_SORTS.find((s) => s.value === stored)?.value ?? 'created';
  } catch {
    return 'created';
  }
}

function writeSort(sort: TaskSort) {
  try {
    window.localStorage.setItem(SORT_STORAGE_KEY, sort);
  } catch {
    return;
  }
}

export type NewTaskContext = {
  propertyId: string;
  propertyName: string;
  portfolioName: string | null;
};

export type TasksPreview = {
  list: PreviewList;
  // The Custom Tasks page owns the whole page; a tab is one table among others.
  scope: 'page' | 'table';
  onOpenFullPage: (taskId: string) => void;
};

const SELECT_SX = { ...FILTER_CONTROL_SX, minWidth: 140, '& .MuiSelect-select': { py: '6px' } };

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <TextField
      select
      size="small"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      slotProps={{
        select: {
          displayEmpty: true,
          renderValue: (selected) =>
            selected
              ? `${label}: ${options.find((o) => o.value === selected)?.label ?? ''}`
              : label,
        },
        htmlInput: { 'aria-label': label },
      }}
      sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 }, ...SELECT_SX }}
    >
      <MenuItem value="">Any</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function uniqueBy<T>(items: T[], key: (item: T) => string): T[] {
  const seen = new Map<string, T>();
  items.forEach((item) => {
    if (!seen.has(key(item))) seen.set(key(item), item);
  });
  return [...seen.values()];
}

export function TasksView({
  scope,
  showProperty,
  onOpen,
  newTaskContext,
  createdFilter = 'global',
  created: controlledCreated,
  onCreatedChange,
  preview: previewConfig,
}: {
  scope: TaskScope;
  showProperty: boolean;
  onOpen: (task: TaskSummary) => void;
  preview: TasksPreview;
  newTaskContext?: NewTaskContext;
  /** Pages without the global Period filter narrow by created date themselves. */
  createdFilter?: 'global' | 'local';
  created?: CreatedFilter;
  onCreatedChange?: (next: CreatedFilter) => void;
}) {
  const { tasks, error: tasksError, isLoading: tasksLoading } = useTasks(scope);
  const { statuses, error: statusesError, isLoading: statusesLoading } = useTaskStatuses();
  const isLoading = tasksLoading || statusesLoading;
  const error = tasksError ?? statusesError;
  const { filters, setFilter, clearFilters } = useTaskFilters();
  const { isGroupAdmin } = useContext(UserContext).userDetails;
  const [layout, setLayout] = useState<TasksLayout>(readLayout);
  const [sort, setSort] = useState<TaskSort>(readSort);
  const [newOpen, setNewOpen] = useState(false);
  const [statusesOpen, setStatusesOpen] = useState(false);
  const completion = useCompletionPrompts();
  const [ownCreated, setOwnCreated] = useState<CreatedFilter>(ANY_TIME);
  const created = controlledCreated ?? ownCreated;
  const setCreated = onCreatedChange ?? setOwnCreated;
  const {
    selectedFilters: { date: period },
  } = useGlobalFilterContext();

  const inCreated = useMemo(
    () =>
      createdFilter === 'global'
        ? (task: TaskSummary) => shownInPeriod(task, period)
        : (task: TaskSummary) => createdInFilter(task, created),
    [createdFilter, period, created],
  );

  const visible = useMemo(
    () =>
      sortTasks(
        tasks.filter((task) => inCreated(task) && matchesTaskFilters(task, filters)),
        sort,
      ),
    [tasks, filters, inCreated, sort],
  );

  const owners = useMemo(
    () =>
      uniqueBy(
        tasks.flatMap((t) => (t.assignee ? [t.assignee] : [])),
        (p) => p.id,
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [tasks],
  );
  const properties = useMemo(
    () =>
      uniqueBy(
        tasks.map((t) => ({ id: t.propertyId, name: t.propertyName })),
        (p) => p.id,
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [tasks],
  );

  const listRef = useRef<HTMLDivElement>(null);
  const preview = useTableRowPreview({
    list: previewConfig.list,
    rootRef: listRef,
    scope: previewConfig.scope,
  });
  // A task filtered out of view takes its open panel with it.
  const { previewId, close: closePreview } = preview.rowPreview;
  useEffect(() => {
    if (previewId && !visible.some((task) => task.id === previewId)) closePreview();
  }, [previewId, visible, closePreview]);

  const changeLayout = (next: TasksLayout | null) => {
    if (!next) return;
    setLayout(next);
    writeLayout(next);
  };

  const onCreated = (task: TaskDetail) => {
    setNewOpen(false);
    clearFilters();
    setCreated(ANY_TIME);
    onOpen(task);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
        <ViewToggle
          aria-label="Task layout"
          size="small"
          activeView={layout}
          viewList={LAYOUTS}
          handleChange={(_event, next: string | null) => changeLayout(next as TasksLayout | null)}
        />
        {preview.peekSwitch}
        <Box sx={{ width: '1px', height: 20, bgcolor: '#d7dbe0', mx: '4px' }} />
        {showProperty ? (
          <FilterSelect
            label="Property"
            value={filters.propertyId}
            onChange={(value) => setFilter('propertyId', value)}
            options={properties.map((p) => ({ value: p.id, label: p.name }))}
          />
        ) : null}
        <FilterSelect
          label="Owner"
          value={filters.ownerId}
          onChange={(value) => setFilter('ownerId', value)}
          options={owners.map((p) => ({ value: p.id, label: p.name }))}
        />
        <FilterSelect
          label="Follow-up"
          value={filters.followUp}
          onChange={(value) => setFilter('followUp', value as FollowUpFilter | null)}
          options={[
            { value: 'overdue', label: 'Overdue' },
            { value: 'week', label: 'Due this week' },
          ]}
        />
        {createdFilter === 'local' ? (
          <CreatedDateFilter
            value={created}
            onChange={setCreated}
            countFor={(range) => tasks.filter((t) => inCreatedRange(t.createdAt, range)).length}
          />
        ) : null}
        {hasActiveFilters(filters) || created.range !== 'any' ? (
          <Button
            size="small"
            onClick={() => {
              clearFilters();
              setCreated(ANY_TIME);
            }}
            sx={BUTTON_SX}
          >
            Clear
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          size="small"
          value={sort}
          onChange={(e) => {
            const next = e.target.value as TaskSort;
            setSort(next);
            writeSort(next);
          }}
          slotProps={{
            select: {
              renderValue: (selected) =>
                `Sort: ${TASK_SORTS.find((o) => o.value === selected)?.label ?? ''}`,
            },
            htmlInput: { 'aria-label': 'Sort tasks' },
          }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 }, ...SELECT_SX, minWidth: 180 }}
        >
          {TASK_SORTS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Box
          sx={{
            ...FILTER_CONTROL_SX,
            display: 'flex',
            alignItems: 'center',
            pl: 1,
            pr: 1.5,
            width: 220,
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <InputBase
            value={filters.query}
            onChange={(e) => setFilter('query', e.target.value)}
            placeholder="Search tasks…"
            inputProps={{ 'aria-label': 'Search tasks' }}
            sx={{ ml: 1, flex: 1, minWidth: 0, fontSize: 14 }}
          />
        </Box>
        {isGroupAdmin ? (
          <Button
            variant="outlined"
            startIcon={<TuneIcon />}
            onClick={() => setStatusesOpen(true)}
            sx={BUTTON_SX}
          >
            Customize
          </Button>
        ) : null}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewOpen(true)}
          sx={BUTTON_SX}
        >
          New task
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: '40px' }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Box role="alert" sx={{ p: '40px', textAlign: 'center', color: '#c62828', fontSize: 13 }}>
          Couldn&apos;t load tasks. Refresh to try again.
        </Box>
      ) : tasks.length > 0 && visible.length === 0 ? (
        <Box sx={{ p: '40px', textAlign: 'center', color: '#777', fontSize: 13 }}>
          No tasks match these filters.
        </Box>
      ) : (
        <Box ref={listRef} sx={{ minWidth: 0 }}>
          {layout === 'board' ? (
            <TaskBoard
              tasks={visible}
              statuses={statuses}
              onOpen={onOpen}
              showProperty={showProperty}
              onHover={preview.tableProps.onRowHover}
              highlightedId={previewId}
              onCompleted={completion.add}
            />
          ) : (
            <TaskGroupedList
              tasks={visible}
              statuses={statuses}
              showProperty={showProperty}
              onOpen={onOpen}
              onHover={preview.tableProps.onRowHover}
              highlightedId={previewId}
              onCompleted={completion.add}
            />
          )}
        </Box>
      )}
      {preview.enabled ? (
        <TaskPreviewPanel
          rowPreview={preview.rowPreview}
          onOpenFullPage={previewConfig.onOpenFullPage}
          onCompleted={completion.add}
        />
      ) : null}

      {newOpen ? (
        <NewTaskDialog
          open
          propertyId={newTaskContext?.propertyId}
          propertyName={newTaskContext?.propertyName}
          portfolioName={newTaskContext?.portfolioName}
          onClose={() => setNewOpen(false)}
          onCreated={onCreated}
        />
      ) : null}
      <CompletionPrompt task={completion.task} onClose={completion.close} />
      {isGroupAdmin ? (
        <StatusesDialog open={statusesOpen} onClose={() => setStatusesOpen(false)} />
      ) : null}
    </Box>
  );
}
